/**
 * Date formatting utilities that avoid the timezone off-by-one bug.
 *
 * Problem: `new Date('2026-06-08').toLocaleDateString()` parses the ISO string
 * as UTC midnight, then renders in the user's local timezone. In US Central
 * (UTC-5/6) this shifts the display back to 2026-06-07.
 *
 * Fix: parse the YYYY-MM-DD components manually and construct a Date in LOCAL
 * time so the calendar day stays identical to what the DB stored.
 *
 * Use these helpers ONLY for DATE-ONLY (YYYY-MM-DD) fields returned by the
 * Django DateField serializer. Full ISO datetimes already carry TZ info and
 * work correctly with `new Date()` directly.
 */

/** Convert an HH:MM or HH:MM:SS string to 12-hour format (e.g. "3:30 PM"). */
export function formatTime(timeStr) {
  if (!timeStr) return '';
  const [hourStr, minuteStr] = timeStr.split(':');
  const hour = parseInt(hourStr, 10);
  const minute = minuteStr?.slice(0, 2) || '00';
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute} ${ampm}`;
}

/** Convert a YYYY-MM-DD string into a local-time Date object. */
export function parseLocalDate(isoDate) {
  if (!isoDate) return null;
  const [y, m, d] = String(isoDate).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/**
 * Format a YYYY-MM-DD string using toLocaleDateString options, preserving the
 * calendar day regardless of the viewer's timezone.
 * Defaults to `{ month: 'short', day: 'numeric', year: 'numeric' }`.
 */
export function formatLocalDate(
  isoDate,
  opts = { month: 'short', day: 'numeric', year: 'numeric' }
) {
  const dt = parseLocalDate(isoDate);
  if (!dt) return '';
  return dt.toLocaleDateString('en-US', opts);
}
